<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-white">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4">
              <svg class="w-7 h-7 text-blue-600" viewBox="0 0 100 100" fill="currentColor">
                <circle cx="50" cy="50" r="6" />
                <circle cx="25" cy="25" r="3" opacity="0.8" />
                <circle cx="75" cy="25" r="3" opacity="0.8" />
                <circle cx="25" cy="75" r="3" opacity="0.8" />
                <circle cx="75" cy="75" r="3" opacity="0.8" />
                <path
                  d="M50 44 L50 56 M44 50 L56 50"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">ASAB Control Panel</h1>
              <p class="text-blue-100">AI Service Abstraction Bridge</p>
            </div>
          </div>

          <div class="flex items-center space-x-4">
            <div class="flex items-center text-white">
              <div
                class="w-3 h-3 rounded-full animate-pulse mr-2"
                :class="overallStatusColor"
              ></div>
              <span class="text-sm">{{ overallStatusLabel }}</span>
            </div>
            <div class="text-right">
              <div class="text-white text-sm">Welcome back,</div>
              <div class="text-blue-100 font-semibold">{{ user?.email || 'User' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Stat tiles -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card variant="elevated" padding="lg" class="border-l-4 border-blue-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">ASAB Agents</p>
              <p class="text-2xl font-bold text-gray-900">{{ formatCount(counts.agents) }}</p>
              <p class="text-xs text-gray-400">{{ countsLoading ? 'loading…' : updatedLabel }}</p>
            </div>
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card variant="elevated" padding="lg" class="border-l-4 border-purple-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">MCP Servers</p>
              <p class="text-2xl font-bold text-gray-900">{{ formatCount(counts.mcp_servers) }}</p>
              <p class="text-xs text-gray-400">{{ countsLoading ? 'loading…' : updatedLabel }}</p>
            </div>
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
        </Card>

        <Card variant="elevated" padding="lg" class="border-l-4 border-green-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Models</p>
              <p class="text-2xl font-bold text-gray-900">{{ formatCount(counts.models) }}</p>
              <p class="text-xs text-gray-400">{{ countsLoading ? 'loading…' : updatedLabel }}</p>
            </div>
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
          </div>
        </Card>

        <Card variant="elevated" padding="lg" class="border-l-4 border-amber-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Credits Remaining</p>
              <p class="text-2xl font-bold text-gray-900">{{ formatCredits(counts.credits_remaining) }}</p>
              <p class="text-xs text-gray-400">{{ countsLoading ? 'loading…' : updatedLabel }}</p>
            </div>
            <div class="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg class="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 12v-2m0-10a9 9 0 100 18 9 9 0 000-18z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      <!-- Quick actions -->
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <router-link
          to="/app/agents"
          class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 hover:border-blue-300 transition-all duration-200 group"
        >
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-blue-900">Create Agent</h3>
          </div>
          <p class="text-blue-700 mb-4">
            Define a new agent with patterns, tools, and guardrails.
          </p>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- decorative affordance inside a router-link; whole card navigates, this isn't an independent Button -->
          <button class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
            Start
          </button>
        </router-link>

        <router-link
          to="/app/mcp-gateway"
          class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 hover:border-purple-300 transition-all duration-200 group"
        >
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-purple-900">MCP Servers</h3>
          </div>
          <p class="text-purple-700 mb-4">
            Connect MCP tool servers and manage virtual server namespaces.
          </p>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- decorative affordance inside a router-link; whole card navigates, this isn't an independent Button -->
          <button class="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors">
            Manage
          </button>
        </router-link>

        <router-link
          to="/app/api-keys"
          class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 hover:border-green-300 transition-all duration-200 group"
        >
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2H7v-2H4a1 1 0 01-1-1v-4c0-2.632 1.357-5.368 3.857-6.858A8.978 8.978 0 0112 3a9 9 0 019 9v0a9.003 9.003 0 01-4 7.429z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-green-900">Manage API Keys</h3>
          </div>
          <p class="text-green-700 mb-4">
            Issue, rotate, and scope keys with fine-grained permissions.
          </p>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- decorative affordance inside a router-link; whole card navigates, this isn't an independent Button -->
          <button class="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">
            View Keys
          </button>
        </router-link>
      </div>

      <!-- Analytics overview -->
      <div class="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Analytics Overview</h3>
            <p class="text-sm text-gray-500">Last 7 days</p>
          </div>
          <div class="text-sm text-gray-500" v-if="analyticsLoading">Loading analytics…</div>
        </div>

        <div v-if="analyticsError" class="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p class="text-sm text-red-700">{{ analyticsError }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div class="rounded-lg border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Total Requests</p>
            <p class="text-lg font-semibold text-gray-900">{{ formattedAnalytics.totalRequests }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Total Tokens</p>
            <p class="text-lg font-semibold text-gray-900">{{ formattedAnalytics.totalTokens }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Total Cost</p>
            <p class="text-lg font-semibold text-gray-900">{{ formattedAnalytics.totalCost }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Error Rate</p>
            <p class="text-lg font-semibold text-gray-900">{{ formattedAnalytics.errorRate }}</p>
          </div>
          <div class="rounded-lg border border-gray-200 p-4">
            <p class="text-xs text-gray-500">P95 Latency</p>
            <p class="text-lg font-semibold text-gray-900">{{ formattedAnalytics.p95Latency }}</p>
          </div>
        </div>
      </div>

      <!-- Recent activity + System health -->
      <div class="grid lg:grid-cols-3 gap-6">
        <!-- Recent Activity — real data from /v1/analytics/events -->
        <div class="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <span class="text-xs text-gray-400" v-if="activityLoading">loading…</span>
          </div>

          <div v-if="recentActivity.length === 0 && !activityLoading" class="text-sm text-gray-500">
            No activity in the last 24h.
          </div>

          <div v-else class="space-y-3">
            <Card
              v-for="ev in recentActivity"
              :key="ev.id"
              padding="sm"
              hover
              class="flex items-center"
            >
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center mr-4"
                :class="activityIconBg(ev)"
              >
                <svg class="w-5 h-5" :class="activityIconColor(ev)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">{{ ev.title }}</p>
                <p class="text-xs text-gray-500 truncate">{{ ev.subtitle }}</p>
              </div>
              <div class="text-xs text-gray-400 ml-2 whitespace-nowrap">{{ ev.timeLabel }}</div>
            </Card>
          </div>
        </div>

        <!-- System Health — real data from /v1/analytics/health/overview -->
        <div class="bg-white rounded-xl shadow-lg p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">System Health</h3>
            <span class="text-xs text-gray-400" v-if="healthLoading">loading…</span>
          </div>

          <div class="space-y-4">
            <div>
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">Gateway Uptime</span>
                <span class="text-green-600 font-medium">{{ pct(health.gateway_uptime) }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-green-500 h-2 rounded-full" :style="barStyle(health.gateway_uptime)"></div>
              </div>
            </div>

            <div>
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">Agent Success Rate</span>
                <span class="text-blue-600 font-medium">{{ pct(health.agent_success_rate) }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-blue-500 h-2 rounded-full" :style="barStyle(health.agent_success_rate)"></div>
              </div>
            </div>

            <div>
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">MCP Tool Success</span>
                <span class="text-purple-600 font-medium">{{ pct(health.tool_success_rate) }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-purple-500 h-2 rounded-full" :style="barStyle(health.tool_success_rate)"></div>
              </div>
            </div>

            <div>
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">Error-Free Rate</span>
                <span class="text-red-600 font-medium">{{ pct(health.error_rate_inverted) }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="bg-red-500 h-2 rounded-full" :style="barStyle(health.error_rate_inverted)"></div>
              </div>
            </div>
          </div>

          <div
            class="mt-6 p-4 rounded-lg border"
            :class="health.degraded_services > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'"
          >
            <div class="flex items-center">
              <svg
                class="w-5 h-5 mr-2"
                :class="health.degraded_services > 0 ? 'text-amber-600' : 'text-green-600'"
                fill="currentColor" viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              <span
                class="text-sm font-medium"
                :class="health.degraded_services > 0 ? 'text-amber-800' : 'text-green-800'"
              >
                {{ health.degraded_services > 0
                  ? `${health.degraded_services} service(s) degraded`
                  : 'All systems healthy' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { z } from 'zod';
  import { useNotification, Card } from '@asabdev/asab-ui';
  import { analyticsApi } from '@/lib/api';
  import { useAuthStore } from '@/stores/auth';
import { toUserMessage } from '@/lib/api/errors';

  interface ResourceCounts {
    agents: number | null;
    mcp_servers: number | null;
    models: number | null;
    credits_remaining: number | null;
  }

  interface HealthOverview {
    gateway_uptime: number | null;
    agent_success_rate: number | null;
    tool_success_rate: number | null;
    error_rate_inverted: number | null;
    degraded_services: number;
    services: { name: string; ok: boolean; latency_ms: number }[];
  }

  interface AnalyticsSummary {
    total_requests: number | null;
    total_tokens: number | null;
    total_cost: number | null;
    error_rate: number | null;
    p95_latency_ms: number | null;
  }

  interface ActivityRow {
    id: string;
    title: string;
    subtitle: string;
    timeLabel: string;
    statusCode: number | null;
  }

  const authStore = useAuthStore();
  const user = computed(() => authStore.user);
  const { showError } = useNotification();

  // ── State ─────────────────────────────────────────────────────────────
  const counts = ref<ResourceCounts>({
    agents: null,
    mcp_servers: null,
    models: null,
    credits_remaining: null,
  });
  const countsLoading = ref(false);
  const updatedAt = ref<Date | null>(null);

  const health = ref<HealthOverview>({
    gateway_uptime: null,
    agent_success_rate: null,
    tool_success_rate: null,
    error_rate_inverted: null,
    degraded_services: 0,
    services: [],
  });
  const healthLoading = ref(false);

  const analyticsLoading = ref(false);
  const analyticsError = ref<string | null>(null);
  const analyticsSummary = ref<AnalyticsSummary>({
    total_requests: null,
    total_tokens: null,
    total_cost: null,
    error_rate: null,
    p95_latency_ms: null,
  });

  const recentActivity = ref<ActivityRow[]>([]);
  const activityLoading = ref(false);

  // ── Schemas ───────────────────────────────────────────────────────────
  const countsSchema = z
    .object({
      agents: z.number().optional(),
      mcp_servers: z.number().optional(),
      models: z.number().optional(),
      credits_remaining: z.number().optional(),
    })
    .passthrough();

  const healthSchema = z
    .object({
      gateway_uptime: z.number().nullable().optional(),
      agent_success_rate: z.number().nullable().optional(),
      tool_success_rate: z.number().nullable().optional(),
      error_rate_inverted: z.number().nullable().optional(),
      degraded_services: z.number().optional(),
      services: z
        .array(
          z.object({
            name: z.string(),
            ok: z.boolean(),
            latency_ms: z.number(),
          })
        )
        .optional(),
    })
    .passthrough();

  const dashboardSummarySchema = z
    .object({
      total_requests: z.number().optional(),
      total_tokens: z.number().optional(),
      total_cost: z.number().optional(),
      error_rate: z.number().optional(),
      p95_latency_ms: z.number().optional(),
    })
    .passthrough();

  const eventsSchema = z
    .object({
      items: z
        .array(
          z
            .object({
              request_id: z.string().optional(),
              time: z.string().optional(),
              service: z.string().optional(),
              action: z.string().optional(),
              model: z.string().nullable().optional(),
              user_id: z.string().nullable().optional(),
              status_code: z.number().nullable().optional(),
              latency_ms: z.number().optional(),
            })
            .passthrough()
        )
        .optional(),
    })
    .passthrough();

  // ── Formatters ─────────────────────────────────────────────────────────
  const formatCount = (n: number | null): string =>
    n === null ? '—' : new Intl.NumberFormat().format(n);

  const formatCredits = (n: number | null): string => {
    if (n === null) return '—';
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
  };

  const formatNumber = (v: number | null): string =>
    v === null ? '—' : new Intl.NumberFormat().format(v);

  const formatCurrency = (v: number | null): string =>
    v === null
      ? '—'
      : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v);

  const formatPercent = (v: number | null): string =>
    v === null ? '—' : `${(v * 100).toFixed(2)}%`;

  const formatLatency = (v: number | null): string =>
    v === null ? '—' : `${Math.round(v)} ms`;

  const formattedAnalytics = computed(() => ({
    totalRequests: formatNumber(analyticsSummary.value.total_requests),
    totalTokens: formatNumber(analyticsSummary.value.total_tokens),
    totalCost: formatCurrency(analyticsSummary.value.total_cost),
    errorRate: formatPercent(analyticsSummary.value.error_rate),
    p95Latency: formatLatency(analyticsSummary.value.p95_latency_ms),
  }));

  const pct = (v: number | null): string => (v === null ? '—' : `${Math.round(v * 100)}%`);
  const barStyle = (v: number | null): string =>
    v === null ? 'width: 0%' : `width: ${Math.max(0, Math.min(100, v * 100))}%`;

  const overallStatusLabel = computed(() =>
    health.value.degraded_services > 0 ? 'Degraded' : 'Operational'
  );
  const overallStatusColor = computed(() =>
    health.value.degraded_services > 0 ? 'bg-amber-400' : 'bg-green-400'
  );

  const updatedLabel = computed(() => {
    if (!updatedAt.value) return '';
    const secs = Math.max(0, Math.round((Date.now() - updatedAt.value.getTime()) / 1000));
    if (secs < 60) return `updated ${secs}s ago`;
    return `updated ${Math.round(secs / 60)}m ago`;
  });

  const relativeTime = (iso: string): string => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return 'unknown';
    const delta = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (delta < 60) return `${delta}s ago`;
    if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
    if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
    return `${Math.round(delta / 86400)}d ago`;
  };

  const activityIconBg = (ev: ActivityRow): string => {
    if (ev.statusCode !== null && ev.statusCode >= 400) return 'bg-red-100';
    return 'bg-blue-100';
  };
  const activityIconColor = (ev: ActivityRow): string => {
    if (ev.statusCode !== null && ev.statusCode >= 400) return 'text-red-600';
    return 'text-blue-600';
  };

  const toIso = (d: Date): string => d.toISOString();

  // ── Loaders ───────────────────────────────────────────────────────────
  const loadCounts = async (): Promise<void> => {
    countsLoading.value = true;
    try {
      const res = await analyticsApi.getResourceCounts();
      const parsed = countsSchema.safeParse(res);
      if (parsed.success) {
        counts.value = {
          agents: parsed.data.agents ?? 0,
          mcp_servers: parsed.data.mcp_servers ?? 0,
          models: parsed.data.models ?? 0,
          credits_remaining: parsed.data.credits_remaining ?? 0,
        };
        updatedAt.value = new Date();
      }
    } catch (error: unknown) {
      showError(toUserMessage(error, 'Failed to load resource counts.'));
    } finally {
      countsLoading.value = false;
    }
  };

  const loadHealth = async (): Promise<void> => {
    healthLoading.value = true;
    try {
      const res = await analyticsApi.getHealthOverview();
      const parsed = healthSchema.safeParse(res);
      if (parsed.success) {
        health.value = {
          gateway_uptime: parsed.data.gateway_uptime ?? null,
          agent_success_rate: parsed.data.agent_success_rate ?? null,
          tool_success_rate: parsed.data.tool_success_rate ?? null,
          error_rate_inverted: parsed.data.error_rate_inverted ?? null,
          degraded_services: parsed.data.degraded_services ?? 0,
          services: parsed.data.services ?? [],
        };
      }
    } catch {
      // Non-fatal — leave defaults.
    } finally {
      healthLoading.value = false;
    }
  };

  const loadAnalyticsSummary = async (): Promise<void> => {
    analyticsLoading.value = true;
    analyticsError.value = null;
    const now = new Date();
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    try {
      const res = await analyticsApi.getDashboardSummary({ start: toIso(start), end: toIso(now) });
      const parsed = dashboardSummarySchema.safeParse(res);
      if (parsed.success) {
        analyticsSummary.value = {
          total_requests: parsed.data.total_requests ?? null,
          total_tokens: parsed.data.total_tokens ?? null,
          total_cost: parsed.data.total_cost ?? null,
          error_rate: parsed.data.error_rate ?? null,
          p95_latency_ms: parsed.data.p95_latency_ms ?? null,
        };
      }
    } catch (error: unknown) {
      analyticsError.value = toUserMessage(error, 'Failed to load analytics.');
    } finally {
      analyticsLoading.value = false;
    }
  };

  const loadActivity = async (): Promise<void> => {
    activityLoading.value = true;
    const now = new Date();
    const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    try {
      const res = await analyticsApi.getEvents({
        start: toIso(start),
        end: toIso(now),
        page: 1,
        page_size: 8,
      });
      const parsed = eventsSchema.safeParse(res);
      if (!parsed.success) {
        recentActivity.value = [];
        return;
      }
      recentActivity.value = (parsed.data.items ?? []).map((e, i) => {
        const action = e.action ?? 'unknown';
        const service = e.service ?? '';
        const model = e.model ?? '';
        const statusCode = e.status_code ?? null;
        return {
          id: e.request_id ?? `ev-${i}`,
          title: action,
          subtitle: [service, model].filter(Boolean).join(' · ') || (e.user_id ?? ''),
          timeLabel: e.time ? relativeTime(e.time) : '',
          statusCode,
        };
      });
    } catch {
      recentActivity.value = [];
    } finally {
      activityLoading.value = false;
    }
  };

  onMounted(() => {
    void loadCounts();
    void loadHealth();
    void loadAnalyticsSummary();
    void loadActivity();
  });
</script>
